import { parse, serialize, type CookieSerializeOptions } from 'cookie-es';

import type { H3Event } from 'h3';
import { getRequestHeader, getCookie, setCookie, deleteCookie } from 'h3';
import destr from 'destr';
import type { NuxtApp } from '#app';

function readCookie<T = string>(app: NuxtApp, name: string, parseFunc?: (cookieValue: string) => T | undefined) {
    const cookie = parseCookieHeader(app)[name];

    if (cookie === undefined) {
        return undefined;
    }

    try {
        const decodedVal = decodeURIComponent(cookie);

        if (parseFunc) {
            return parseFunc(decodedVal);
        }

        return destr<T>(decodedVal);
    } catch {
        return undefined;
    }
}

function writeCookie<T>(app: NuxtApp, name: string, val?: T, opts?: CookieSerializeOptions) {
    if (import.meta.server) {
        writeServerCookie(useRequestEvent(app)!, name, val, { ...opts, path: '/' });
    } else {
        writeClientCookie(name, val, { ...opts, path: '/' });
    }
}

function writeClientCookie<T>(name: string, value?: T, opts?: CookieSerializeOptions) {
    if (import.meta.client) {
        document.cookie =
            value == null ? serialize(name, '', { ...opts, maxAge: -1 }) : serialize(name, encodeCookie(value), opts);
    }
}

function writeServerCookie<T>(event: H3Event, name: string, value?: T, opts?: CookieSerializeOptions) {
    if (!event) {
        return;
    }

    // update if value is set
    if (value != null) {
        return setCookie(event, name, encodeCookie(value), opts);
    }

    // delete if cookie exists in browser and value is null/undefined
    if (getCookie(event, name) !== undefined) {
        return deleteCookie(event, name, opts);
    }

    // else ignore if cookie doesn't exist in browser and value is null/undefined
}

function parseCookieHeader(app: NuxtApp) {
    if (import.meta.server) {
        return parse(getRequestHeader(useRequestEvent(app)!, 'cookie') ?? '');
    }

    return parse(document.cookie);
}

const encodeCookie = <T>(val: T) => encodeURIComponent(typeof val === 'string' ? val : JSON.stringify(val));

export { readCookie, writeCookie };
